/**
 * Tournament step of the cycle configure wizard (drawn only when the tournaments
 * module is on).
 *
 * A tournament narrows a track's items through rounds of ranked sessions. It is a
 * `tournaments` row with its `tournament_rounds`, tied to the cycle and one track.
 * This step lists the tournaments the cycle's open tracks already have, and sets up
 * a new one for a chosen track through `createTournamentWithRounds` — the create
 * call the Tournament Builder makes (src/lib/tournamentSetup.ts). It asks only what
 * the Builder needs that the wizard cannot read: a name, how many rounds, and the
 * ranking method. The items are the track's pool; everyone may take part ("open",
 * the Builder's default). The tournament is left in DRAFT: marking it ready and
 * opening a round happen on its own page, which this step links to. Someone who
 * wants to pick items from the library or name participants uses the Builder,
 * also linked here, with the cycle and track filled in.
 */
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, Trophy } from 'lucide-react';
import { useTournamentStore } from '../../../stores/prioritization/tournamentStore';
import { useItemPoolsStore } from '../../../stores/prioritization/itemPoolsStore';
import { useCycleOpenTracks } from '../../../hooks/prioritization/useCycleOpenTracks';
import { RANKING_TOOLS, createTournamentWithRounds, defaultRoundCount, defaultRounds, usefulRoundCounts } from '../../../lib/tournamentSetup';
import type { Tournament } from '../../../types/prioritization/tournament';
import { Select } from '@/components/ds';

export interface CycleTournamentPanelProps {
  cycleId: string;
  userId: string;
}

const STATE_LABEL: Record<Tournament['state'], string> = {
  DRAFT: 'Draft',
  READY: 'Ready',
  IN_PROGRESS: 'In progress',
  AWAITING_ADVANCE: 'Needs review',
  COMPLETE: 'Complete',
  CANCELLED: 'Cancelled',
};

const builderPath = (cycleId: string, trackId?: string) =>
  `/prioritization/tournaments/new?cycleId=${encodeURIComponent(cycleId)}${trackId ? `&trackId=${encodeURIComponent(trackId)}` : ''}`;

export function CycleTournamentPanel({ cycleId, userId }: CycleTournamentPanelProps) {
  const loadTournaments = useTournamentStore((s) => s.loadTournaments);
  const allTournaments = useTournamentStore((s) => s.tournaments);
  const createTournament = useTournamentStore((s) => s.createTournament);
  const addRound = useTournamentStore((s) => s.addRound);
  const pools = useItemPoolsStore((s) => s.itemPools);
  const fetchPools = useItemPoolsStore((s) => s.fetchFromSupabase);
  const { tracks } = useCycleOpenTracks(cycleId);

  useEffect(() => {
    // All of the workspace's: one set up from a track page carries its track and no cycle.
    void loadTournaments();
    void fetchPools();
  }, [loadTournaments, fetchPools]);

  const tournamentsOf = (trackId: string) =>
    Object.values(allTournaments).filter((t) => t.trackId === trackId);
  const cycleOnly = Object.values(allTournaments).filter((t) => t.cycleId === cycleId && !t.trackId);

  const [trackId, setTrackId] = useState<string>('');
  const chosenTrack = tracks.find((t) => t.id === trackId) ?? tracks[0];
  const poolItemIds = useMemo(
    () => (chosenTrack ? pools.find((p) => (p as { trackId?: string }).trackId === chosenTrack.id)?.priorityIds ?? [] : []),
    [pools, chosenTrack],
  );

  const [name, setName] = useState('');
  // Rounds follow the pool: a final always receives at least two items.
  const roundChoices = usefulRoundCounts(poolItemIds.length);
  const [chosenRounds, setRoundCount] = useState<number | null>(null);
  const roundCount = chosenRounds !== null && roundChoices.includes(chosenRounds)
    ? chosenRounds
    : defaultRoundCount(poolItemIds.length);
  const [toolId, setToolId] = useState<string>('stack_ranking');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<Tournament | null>(null);

  const shownName = name || (chosenTrack ? `${chosenTrack.name} tournament` : '');

  const handleCreate = async () => {
    if (!chosenTrack || !shownName.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const tournament = await createTournamentWithRounds(
        {
          name: shownName,
          cycleId,
          trackId: chosenTrack.id,
          facilitatorId: userId,
          itemIds: poolItemIds,
          toolId,
          rounds: defaultRounds(roundCount, poolItemIds.length),
          participantMode: 'open',
        },
        { createTournament, addRound },
      );
      setCreated(tournament);
      setName('');
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-[var(--text-pri)] flex items-center gap-2">
          <Trophy className="h-5 w-5 text-[var(--text-ter)]" />
          Tournament
        </h3>
        <p className="text-sm text-[var(--text-ter)] mt-1">
          A tournament narrows a track&apos;s items through rounds of ranked sessions. Set one up here as a
          draft for one track; you mark it ready and open its first round on the tournament&apos;s page.
          Optional.
        </p>
      </div>

      <section aria-label="This cycle's tournaments" className="border border-[var(--rule)] bg-[var(--surface)] p-3 space-y-2">
        <p className="font-mono text-[0.72rem] uppercase tracking-[0.12em] text-[var(--text-ter)]">This cycle&apos;s tournaments</p>
        {tracks.length === 0 ? (
          <p className="text-sm text-[var(--text-sec)]">This cycle has no open tracks.</p>
        ) : (
          <ul className="space-y-1 text-sm">
            {tracks.map((t) => {
              const list = tournamentsOf(t.id);
              return (
                <li key={t.id} data-testid={`track-tournaments-${t.id}`}>
                  <span className="text-[var(--text-pri)]">{t.name}</span>
                  {list.length === 0 ? (
                    <span className="text-[var(--text-ter)]"> — none</span>
                  ) : (
                    <span>
                      {' — '}
                      {list.map((tn, i) => (
                        <span key={tn.id}>
                          {i > 0 && ', '}
                          <Link to={`/prioritization/tournaments/${tn.id}`} className="underline">{tn.name}</Link>
                          <span className="text-[var(--text-ter)]"> ({STATE_LABEL[tn.state] ?? tn.state})</span>
                        </span>
                      ))}
                    </span>
                  )}
                </li>
              );
            })}
            {cycleOnly.map((tn) => (
              <li key={tn.id}>
                <span className="text-[var(--text-pri)]">The cycle</span>
                {' — '}
                <Link to={`/prioritization/tournaments/${tn.id}`} className="underline">{tn.name}</Link>
                <span className="text-[var(--text-ter)]"> ({STATE_LABEL[tn.state] ?? tn.state})</span>
              </li>
            ))}
          </ul>
        )}
      </section>

      {created && (
        <p role="status" className="text-sm text-[var(--green)]">
          Set up {created.name} as a draft.{' '}
          <Link to={`/prioritization/tournaments/${created.id}`} className="underline">
            Open it to check the bracket, mark it ready and open the first round.
          </Link>
        </p>
      )}

      {chosenTrack && (
        <section aria-label="Set up a tournament" className="border border-[var(--rule)] bg-[var(--bg-raised)] p-4 space-y-4">
          <label className="block text-sm">
            <span className="text-[var(--text-sec)]">Track</span>
            <Select
              value={chosenTrack.id}
              onChange={(e) => setTrackId(e.target.value)}
              className="mt-1 w-full px-2 py-1.5 border border-[var(--rule)] bg-[var(--bg)]"
            >
              {tracks.map((t) => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </Select>
          </label>

          {poolItemIds.length === 0 ? (
            <p className="text-sm text-[var(--text-sec)]">
              {chosenTrack.name}&apos;s pool holds no items yet, and a tournament here ranks the pool. Add items to
              the track&apos;s pool first, or{' '}
              <Link to={builderPath(cycleId, chosenTrack.id)} className="underline">set it up in the Tournament Builder</Link>
              , where you pick the items from the library.
            </p>
          ) : roundChoices.length === 0 ? (
            <p className="text-sm text-[var(--text-sec)]">
              {chosenTrack.name}&apos;s pool holds {poolItemIds.length} item{poolItemIds.length === 1 ? '' : 's'}. A
              tournament narrows at least three down to a final of two or more; add items to the pool first.
            </p>
          ) : (
            <>
              <p className="text-sm text-[var(--text-sec)]">
                Ranks the {poolItemIds.length} item{poolItemIds.length === 1 ? '' : 's'} in {chosenTrack.name}&apos;s pool. Anyone in the
                cycle may take part.
              </p>
              <label className="block text-sm">
                <span className="text-[var(--text-sec)]">Name</span>
                <input
                  value={shownName}
                  onChange={(e) => setName(e.target.value)}
                  className="mt-1 w-full px-2 py-1.5 border border-[var(--rule)] bg-[var(--bg)]"
                />
              </label>
              <div className="flex flex-wrap gap-4">
                <label className="text-sm">
                  <span className="text-[var(--text-sec)]">Rounds</span>
                  <Select
                    value={roundCount}
                    onChange={(e) => setRoundCount(Number(e.target.value))}
                    className="mt-1 block px-2 py-1.5 border border-[var(--rule)] bg-[var(--bg)]"
                  >
                    {roundChoices.map((n) => (
                      <option key={n} value={n}>{n}</option>
                    ))}
                  </Select>
                </label>
                <label className="text-sm flex-1 min-w-[12rem]">
                  <span className="text-[var(--text-sec)]">Ranking method, every round</span>
                  <Select
                    value={toolId}
                    onChange={(e) => setToolId(e.target.value)}
                    className="mt-1 block w-full px-2 py-1.5 border border-[var(--rule)] bg-[var(--bg)]"
                  >
                    {RANKING_TOOLS.map((t) => (
                      <option key={t.id} value={t.id}>{t.label}</option>
                    ))}
                  </Select>
                </label>
              </div>
              <p className="text-xs text-[var(--text-ter)]">
                {defaultRounds(roundCount, poolItemIds.length)
                  .map((r) => `${r.label}: top ${r.advancingCount}`)
                  .join(' → ')}
              </p>
              {error && <p className="text-sm text-[var(--coral)]">Not set up: {error}</p>}
              <div className="flex flex-wrap items-center justify-between gap-3">
                <Link to={builderPath(cycleId, chosenTrack.id)} className="text-sm underline text-[var(--text-sec)]">
                  Pick items or people instead, in the Tournament Builder
                </Link>
                <button
                  type="button"
                  disabled={busy || !shownName.trim()}
                  onClick={() => void handleCreate()}
                  className="btn btn-primary flex items-center gap-2 disabled:opacity-50"
                >
                  {busy && <Loader2 className="h-4 w-4 animate-spin" />}
                  Set up a draft tournament for {chosenTrack.name}
                </button>
              </div>
            </>
          )}
        </section>
      )}
    </div>
  );
}

export default CycleTournamentPanel;
